import React, { useEffect, useState } from "react";
import { applySortFilter, isEmptyFilter } from "../../utils/tableFunctions";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Grid,
  TextField,
} from "@mui/material";
import ListHead from "../table/ListHead";
import Scrollbar from "../table/Scrollbar";
import MoreMenu from "./MoreMenu";
import EditDialog from "./Dialogs/EditDialog";
import DeleteDialog from "./Dialogs/DeleteDialog";
import {
  getTerminalFilterConditions,
  Log,
  sendDataWithPost,
  TerminalActions,
  getPaymentBrandOptions,
  getPaymentHostOptions,
} from "../../services/services";
import Label from "../Label";
import { StyledTableRow } from "../TableRow";
import FormSearch from "../FormSearch";
import CircularLoader from "../circularLoader";
import SearchPrompt from "../SearchPrompt";
import { pageNumber, rowsPerPage } from "../../utils/constants";
import SearchNotFound from "../table/SearchNotFound";
import Pagination from "../Pagination";
import FormDialog from "../FormDialog";
import { useFormik } from "formik";
import * as yup from "yup";
import CommonDialog from "../CustomDialog";
import TerminalDetails from "../../pages/details/admin/TerminalDetails";
import HeaderHelpPage from "../HeaderHelpPage";

const defaultValue = {
  is_deleted: false
}

const TABLE_HEAD = [
  { id: "terminal_id", label: "Terminal ID" },
  { id: "merchantName", label: "Merchant" },
  { id: "business_entity", label: "Business Entity" },
  { id: "mode", label: "Mode" },
  { id: "is_active", label: "Deleted" },
  { id: "edit" },
];

const ViewTerminal = ({ userRole }) => {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  const [loaded, setLoaded] = useState(false);

  const [pagination, setPagination] = useState({
    page: pageNumber,
    page_size: rowsPerPage,
  });
  const [tableData, setTableData] = useState({ results: [] });
  const [filter, setFilter] = useState({ is_active: true });
  const [inputValues, setInputValues] = useState({});
  // the list is only queried once the user has searched, so the tab opens fast
  const [searched, setSearched] = useState(false);

  // dialogs
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openSuspendDialog, setOpenSuspendDialog] = useState(false);
  const [openUnsuspendDialog, setOpenUnsuspendDialog] = useState(false);
  const SuspendTerminalDialog = ({
    open,
    selectedId,
  }) => {
    const { handleChange, handleSubmit, handleBlur, values, touched, errors, isSubmitting, resetForm } = useFormik({
      initialValues: { reason: '' },
      validationSchema: yup.object().shape({ reason: yup.string().required('Required').trim() }),
      onSubmit: async (vals) => {
        Log.info(`Click button [Submit] - Suspend Terminal by Id: ${selectedId}`, vals);
        try {
          await TerminalActions('Suspend', { pk: selectedId, action_message: vals.reason }, () => queryPage(pagination));
        } finally {
          handleClose();
          resetForm();
        }
      }
    });

    const handleClose = () => {
      setOpenSuspendDialog(false);
      resetForm();
    };

    return (
      <FormDialog
        open={open}
        onClose={handleClose}
        title="Suspend this terminal?"
        handleSubmit={handleSubmit}
        loading={isSubmitting}
        maxWidth="sm"
      >
        <Grid container sx={{ alignItems: 'center' }} spacing={3}>
          <Grid item md={5} xs={5}>
            <Typography variant="subtitle1">Reason *</Typography>
          </Grid>
          <Grid item md={7} xs={7}>
            <TextField
              name="reason"
              value={values.reason}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="Please enter your reason"
              error={Boolean(touched.reason && errors.reason)}
              helperText={touched.reason && errors.reason}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </FormDialog>
    );
  };
  
  const UnsuspendTerminalDialog = ({
    open,
    selectedId,
  }) => {
    const { handleChange, handleSubmit, handleBlur, values, touched, errors, isSubmitting, resetForm } = useFormik({
      initialValues: { reason: '' },
      validationSchema: yup.object().shape({ reason: yup.string().required('Required').trim() }),
      onSubmit: async (vals) => {
        Log.info(`Click button [Submit] - Unsuspend Terminal by Id: ${selectedId}`, vals);
        try {
          await TerminalActions('Unsuspend', { pk: selectedId, action_message: vals.reason }, () => queryPage(pagination));
        } finally {
          handleClose();
          resetForm();
        }
      }
    });

    const handleClose = () => {
      setOpenUnsuspendDialog(false);
      resetForm();
    };

    return (
      <FormDialog
        open={open}
        onClose={handleClose}
        title="Unsuspend this terminal?"
        handleSubmit={handleSubmit}
        loading={isSubmitting}
        maxWidth="sm"
      >
        <Grid container sx={{ alignItems: 'center' }} spacing={3}>
          <Grid item md={5} xs={5}>
            <Typography variant="subtitle1">Reason *</Typography>
          </Grid>
          <Grid item md={7} xs={7}>
            <TextField
              name="reason"
              value={values.reason}
              onChange={handleChange}
              onBlur={handleBlur}
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={2}
              placeholder="Please enter your reason"
              error={Boolean(touched.reason && errors.reason)}
              helperText={touched.reason && errors.reason}
              disabled={isSubmitting}
            />
          </Grid>
        </Grid>
      </FormDialog>
    );
  };
  
  const [selectedId, setSelectedId] = useState();
  const [itemId, setItemId] = useState(null);

  const CustomTableCell = ({ align, field, id }) => {
  return (
    <TableCell
      align={align}
      onClick={() => {
        // window.open(`/3/app/terminal-details/${id}/`, "_blank");
        setItemId(id);
      }}
    >
      {field}
    </TableCell>
  );
};

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const queryPage = (queryParams, params) => {
    const queryString = new URLSearchParams(queryParams).toString();
    const url = `/merchant/terminal/filter/?${queryString}`;
    loaded && setLoaded(false);
    sendDataWithPost(url, params || filter, renderData, setLoaded, () => {
      setPagination(queryParams);
    });
  };

  const handleChangePage = (event, newPage) => {
    queryPage({
      page: newPage + 1,
      page_size: pagination.page_size,
    });
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage({
      page: 1,
      page_size: event.target.value,
    })
  };

  const filteredList = applySortFilter(
    tableData.results || [],
    order,
    orderBy
  );

  const isItemNotFound = tableData.results.length === 0;

  const renderData = res => {
    const newData = res.results.map(item => ({
      ...item,
      merchantName: item.merchant?.name,
      business_entity: item.merchant?.business_entity?.business_name,
      mode: item.mode?.mode,
      // is_suspended: item.is_suspended || false
    }));
    
    setTableData({
      ...res,
      results: newData
    });
  }

  useEffect(() => {
    getTerminalFilterConditions(setTerminalFilter, setFLoaded);
  }, []);

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setOpenSuspendDialog(false);
    setOpenUnsuspendDialog(false);
    selectedId && setSelectedId();
  };

  // filter
  const [terminalFilter, setTerminalFilter] = useState({});
  const [fLoaded, setFLoaded] = useState(false);

  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [hostOptions, setHostOptions] = useState([]);

  const handleTerminalFilterChange = (data) => {
    const methodId = data?.payment_method?.id || null;
    const brandId = data?.payment_brand?.id || null;

    if (methodId !== selectedMethod) {
      setSelectedMethod(methodId);
      setSelectedBrand(null);
      setHostOptions([]);
      if (methodId) {
        getPaymentBrandOptions((res) => {
          setBrandOptions(res?.map((b) => ({ id: b.id, label: b.name })) || []);
        }, null, methodId);
      } else {
        setBrandOptions([]);
      }
    }

    if (brandId !== selectedBrand) {
      setSelectedBrand(brandId);
      if (brandId) {
        getPaymentHostOptions((res) => {
          setHostOptions(res?.map((h) => ({ id: h.id, label: h.name })) || []);
        }, null, brandId);
      } else {
        setHostOptions([]);
      }
    }
  };

  const onFilterSubmit = (params) => {
    // searching with an empty form falls back to the filter the tab used to
    // load with on first render
    const data = isEmptyFilter(params) ? { ...defaultValue } : params;
    const newPrams = {
      ...data,
      business_entity: data.business_entity?.id,
      merchant: data.merchant?.id,
      terminal_id: data.terminal_id,
      payment_method: data.payment_method?.id,
      payment_brand: data.payment_brand?.id,
      payment_host: data.payment_host?.id,
      is_active: (data.is_deleted === 'all' ? undefined : !data.is_deleted),
      terminal_id_input: inputValues?.terminal_id?.trim() || null,
      merchant_input: inputValues?.merchant?.trim() || null,
      business_entity_input: inputValues?.business_entity?.trim() || null
    }
    const newPagination = {
      ...pagination,
      page: 1,
    };

    if (newPrams.is_active === 'all') delete newPrams.is_active;
    Log.info("Click button [Apply Filter] - Search Terminal list", newPrams)

    setSearched(true);
    setFilter(newPrams);
    queryPage(newPagination, newPrams);
  };

  const schema = [
    {
      type: "search-select",
      label: "Terminal ID",
      param: "terminal_id",
      options: terminalFilter?.terminal_id?.map(terminal_id => terminal_id),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Merchant",
      param: "merchant",
      options: terminalFilter?.merchant?.map((merchant) => ({
        label: merchant.merchant__name,
        id: merchant.merchant__id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Business Entity",
      param: "business_entity",
      options: terminalFilter?.business_entity?.map((entity) => ({
        label: entity.merchant__business_entity__business_name,
        id: entity.merchant__business_entity__id,
      })),
      inputValues,
      setInputValues,
    },
    {
      type: "search-select",
      label: "Method",
      param: "payment_method",
      options: terminalFilter?.payment_method?.map((m) => ({ label: m.name, id: m.id }))
    },
    {
      type: "search-select",
      label: "Brand",
      param: "payment_brand",
      options:
        (selectedMethod ? brandOptions : terminalFilter?.payment_brand?.map((b) => ({ label: b.name, id: b.id })))
    },
    {
      type: "search-select",
      label: "Host",
      param: "payment_host",
      options: 
      (selectedBrand ? hostOptions : selectedMethod ? [] : terminalFilter?.payment_host?.map((h) => ({ 
        label: h.name, id: h.id 
      })))
    },
    {
      type: "select",
      label: "Deleted",
      param: "is_deleted",
      id: "true",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    }
  ]

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={!fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
        onChangeBusinessEntity={handleTerminalFilterChange}
      />
      {!searched ?
        <SearchPrompt />
        : loaded ?
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  onRequestSort={handleRequestSort}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList.map((row) => {
                        const { id, terminal_id, merchantName, is_active, mode, business_entity } =
                          row;

                        return (
                          <StyledTableRow hover key={id}>
                            <CustomTableCell field={terminal_id} id={id} />
                            <CustomTableCell field={merchantName} id={id} />
                            <CustomTableCell field={business_entity} id={id} />
                            <CustomTableCell field={mode} id={id} />
                            <CustomTableCell
                              field={
                                <Label
                                  variant="ghost"
                                  color={!is_active ? "error" : "success"}
                                >
                                  {!is_active ? "YES" : "NO"}
                                </Label>
                              }
                              id={id}
                            />

                            <TableCell align="right">
                              { (localStorage.getItem('role') === 'admin' || (is_active && (userRole.includes("Edit") ||
                                userRole.includes("Delete")))) && (
                                <MoreMenu
                                  setOpenDeleteDialog={setOpenDeleteDialog}
                                  setOpenEditDialog={setOpenEditDialog}
                                  setOpenSuspendDialog={setOpenSuspendDialog}
                                  setOpenUnsuspendDialog={setOpenUnsuspendDialog}
                                  setRow={() => setSelectedId(id)}
                                  userRole={userRole}
                                  // is_suspended={is_suspended}
                                />
                              )}
                            </TableCell>
                          </StyledTableRow>
                        );
                      })}
                </TableBody>
                {isItemNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <SearchNotFound />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isItemNotFound && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
        :
        <CircularLoader />
      }
      {selectedId && (
        <>
          {openEditDialog && (
            <EditDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setTableLoaded={setLoaded}
            />
          )}
          {openDeleteDialog && (
            <DeleteDialog
              open={true}
              onClose={handleCloseDialog}
              selectedId={selectedId}
              setResponse={renderData}
              setLoaded={setLoaded}
            />
          )}
          {openSuspendDialog && (
            <SuspendTerminalDialog
             open={true}
             selectedId={selectedId}
           />
          )}
          {openUnsuspendDialog && (
            <UnsuspendTerminalDialog
             open={true}
             selectedId={selectedId}
           />
          )}
        </>
      )}

      {itemId && (
        <CommonDialog
          title={<HeaderHelpPage title="Terminal Details" variant="h5" mb={0} />}
          onClose={() => setItemId(null)}
        >
          <TerminalDetails itemId={itemId} />
        </CommonDialog>
      )}
    </Box>
  );
};

export default ViewTerminal;